import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { analytics } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlanIntentModal = ({ isOpen, onClose, planName, planPrice }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/plan-intents`, {
        email: email.toLowerCase().trim(),
        plan: planName.toLowerCase(),
        source: 'pricing_page'
      });
      
      analytics.planIntent(planName.toLowerCase());
      setIsSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || d).join(', '));
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0b0e1a] border border-white/10 rounded-2xl p-8 w-full max-w-md shadow-2xl">
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          data-testid="modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {isSuccess ? (
          // Success state
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-[#f2e2a6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#f2e2a6]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">You're on the list!</h3>
            <p className="text-gray-400 mb-6">
              We'll notify you at <span className="text-white">{email}</span> when we're ready to accept new {planName} subscribers.
            </p>
            <Button
              onClick={handleClose}
              className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold"
              data-testid="modal-done"
            >
              Got it
            </Button>
          </div>
        ) : (
          // Form state
          <>
            <div className="text-center mb-6">
              <div className="inline-block bg-[#f2e2a6]/20 px-3 py-1 rounded-full mb-4">
                <span className="text-[#f2e2a6] text-sm font-semibold">{planName} Plan</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Get notified when we launch</h3>
              <p className="text-gray-400 text-sm">
                We're not accepting payments yet. Leave your email and we'll let you know when the {planName} plan is available.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm" data-testid="intent-error">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-gray-400 text-sm mb-2">Email address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="intent-email"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="intent-submit"
                className="w-full bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold py-6"
              >
                {isSubmitting ? 'Saving...' : 'Notify Me'}
              </Button>

              <p className="text-gray-500 text-xs text-center">
                No spam, just a notification when we're ready.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanIntentModal;
